import {
  clamp,
  mat3Invert,
  mat3Multiply,
  mat3MultiplyAll,
  mat3PureTranslation,
  mat3Rotation,
  mat3Scaling,
  mat3Translation,
  type Mat3,
} from '../core/math';
import type { Point, ViewState } from '../core/types';

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 32;

export interface CanvasSize {
  /** CSS pixels. The device-pixel drawing buffer only affects the GL viewport. */
  width: number;
  height: number;
}

/**
 * Builds the matrix that maps the unit document quad to clip space. The Y axis is
 * flipped here — and only here — because document row 0 must appear at the top of
 * the screen while clip space grows upwards.
 */
export function buildViewMatrix(
  view: ViewState,
  canvas: CanvasSize,
  docWidth: number,
  docHeight: number,
): Mat3 {
  const radians = ((view.rotation ?? 0) * Math.PI) / 180;
  // Rotation happens in screen space, where the pinned document point is the
  // origin, so it slots between the flip to clip space and the zoom.
  return mat3MultiplyAll(
    mat3Scaling(2 / canvas.width, -2 / canvas.height),
    mat3Rotation(radians),
    mat3Scaling(view.zoom, view.zoom),
    mat3Translation(-view.centerX, -view.centerY),
    mat3Scaling(docWidth, docHeight),
  );
}

/**
 * Converts a document-space transform into the destination-UV to source-UV matrix
 * that `composite.frag` and `copy.frag` expect: `S⁻¹ · M⁻¹ · S`.
 */
export function docMatrixToSrcUv(matrix: Mat3, docWidth: number, docHeight: number): Mat3 {
  return mat3MultiplyAll(
    mat3Scaling(1 / docWidth, 1 / docHeight),
    mat3Invert(matrix),
    mat3Scaling(docWidth, docHeight),
  );
}

/** Destination-UV → source-UV matrix for a layer's contentOffset, or null if none. */
export function contentOffsetSrcMatrix(
  offset: Point,
  docWidth: number,
  docHeight: number,
): Mat3 | null {
  if (offset.x === 0 && offset.y === 0) return null;
  return docMatrixToSrcUv(mat3Translation(offset.x, offset.y), docWidth, docHeight);
}

/**
 * Combines a permanent contentOffset src-matrix with a live transform src-matrix.
 * Order is offset then live in document space: `offsetSrc · liveSrc`.
 */
export function composeSrcMatrices(offsetSrc: Mat3 | null, liveSrc: Mat3 | null): Mat3 | null {
  if (!offsetSrc) return liveSrc;
  if (!liveSrc) return offsetSrc;
  return mat3Multiply(offsetSrc, liveSrc);
}

/**
 * Translation encoded in a destination-UV → source-UV matrix produced by
 * `docMatrixToSrcUv`, or null when the transform is not a pure shift.
 */
export function srcUvPureTranslation(
  srcMatrix: Mat3,
  docWidth: number,
  docHeight: number,
): Point | null {
  const doc = mat3MultiplyAll(
    mat3Scaling(docWidth, docHeight),
    mat3Invert(srcMatrix),
    mat3Scaling(1 / docWidth, 1 / docHeight),
  );
  return mat3PureTranslation(doc);
}

export function screenToDocument(point: Point, view: ViewState, canvas: CanvasSize): Point {
  const radians = ((view.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = (point.x - canvas.width / 2) / view.zoom;
  const dy = (point.y - canvas.height / 2) / view.zoom;
  return {
    x: view.centerX + dx * cos + dy * sin,
    y: view.centerY - dx * sin + dy * cos,
  };
}

export function documentToScreen(point: Point, view: ViewState, canvas: CanvasSize): Point {
  const radians = ((view.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = (point.x - view.centerX) * view.zoom;
  const dy = (point.y - view.centerY) * view.zoom;
  return {
    x: canvas.width / 2 + dx * cos - dy * sin,
    y: canvas.height / 2 + dx * sin + dy * cos,
  };
}

/** Zoom that fits the document in the viewport with a small margin. */
export function fitZoom(
  canvas: CanvasSize,
  docWidth: number,
  docHeight: number,
  margin = 32,
): number {
  const available = {
    width: Math.max(1, canvas.width - margin * 2),
    height: Math.max(1, canvas.height - margin * 2),
  };
  const zoom = Math.min(available.width / docWidth, available.height / docHeight);
  return clamp(Math.min(zoom, 1), MIN_ZOOM, MAX_ZOOM);
}

/** Zooms while keeping the document point currently under `anchor` in place. */
export function zoomAt(
  view: ViewState,
  canvas: CanvasSize,
  anchor: Point,
  nextZoom: number,
): ViewState {
  const zoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
  const before = screenToDocument(anchor, view, canvas);
  const after = screenToDocument(anchor, { ...view, zoom }, canvas);
  return {
    ...view,
    zoom,
    centerX: view.centerX + (before.x - after.x),
    centerY: view.centerY + (before.y - after.y),
  };
}

export const ZOOM_STEPS = [
  0.02, 0.03, 0.05, 0.07, 0.1, 0.15, 0.25, 0.33, 0.5, 0.67, 1, 1.5, 2, 3, 4, 6, 8, 12, 16, 24, 32,
];

export function nextZoomStep(zoom: number, direction: 1 | -1): number {
  if (direction > 0) {
    return ZOOM_STEPS.find((z) => z > zoom * 1.001) ?? MAX_ZOOM;
  }
  const lower = ZOOM_STEPS.filter((z) => z < zoom * 0.999);
  return lower.length ? lower[lower.length - 1] : MIN_ZOOM;
}
